import { useQuery } from '@tanstack/react-query';
import { useShellSearchParams } from './use-shell-search-params';
import { useLocale } from '../i18n/locale-context';
import { fetchSearchFacets, type SearchFacets } from './catalog-client';

/**
 * Scope + kind facet counts for the ACTIVE search query, read off the URL — via
 * `useShellSearchParams`, so a prerendered page hydrates before the query
 * applies. Shared by the kind tabs (which otherwise can't count a query) and the
 * scope filter chips — both call this hook, so React Query dedupes the single
 * `['search', 'facets', …]` request between them rather than firing two.
 *
 * Disabled when there is no query (`enabled: q.length > 0`): the facet endpoint
 * requires `q`, and browse counts come from their own per-facet endpoints.
 * `placeholderData` keeps the last numbers while a new query settles, so the
 * chips/tabs lag rather than blank on every keystroke.
 *
 * Only the filters the search endpoint honours are forwarded — no scope/kind
 * (the breakdown must span every layer and kind), no sort/range (search ignores
 * them). Every caller reading the URL through the SAME hook is what keeps their
 * query keys byte-identical.
 */
export function useSearchFacets(): {
  facets: SearchFacets | undefined;
  active: boolean;
} {
  // Shell-first, like every other reader in the prerendered catalog tree: on a hard
  // load of `/?q=…` the served HTML is the prerender's (query-less) one, and
  // `active` flipping to true on the hydration render made ScopeChips appear where
  // the server had nothing — a #418 on its own. See lib/use-shell-search-params.ts.
  const [params] = useShellSearchParams();
  const { locale } = useLocale();
  const q = params.get('q')?.trim() ?? '';
  const filters = {
    q,
    region: params.get('region') ?? undefined,
    category: params.get('category') ?? undefined,
    status: params.get('status') ?? undefined,
    ikpu: params.get('ikpu') ?? undefined,
    niche: params.get('niche') ?? undefined,
    customer: params.get('customer') ?? undefined,
    // Минус-слова — ОДНА из осей, которую поиск честно применяет, и единственная
    // отрицательная. Пока её здесь не было, `/?q=трансформатор&exclude=ремонт`
    // показывал бейджи «Все 67 · Товары 35» над списком из 20 лотов, а ссылки
    // сохранённых поисков выглядят ровно так. Часть `filters`, поэтому ключ
    // запроса меняется вместе с ней сам.
    exclude: params.get('exclude') ?? undefined,
    lang: locale,
  };
  const { data } = useQuery({
    queryKey: ['search', 'facets', filters],
    queryFn: () => fetchSearchFacets(filters),
    enabled: q.length > 0,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
  return { facets: data, active: q.length > 0 };
}
